import { Component, EventEmitter, Input, Output, ViewEncapsulation, OnInit, OnDestroy, OnChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EstablishmentsService, CreateQueuePayload, EstablishmentDetailsDto } from '../../core/services/establishments.service';

@Component({
  selector: 'app-queue-create-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './queue-create-modal.component.html',
  styleUrls: ['./queue-create-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None, // réutilise les styles globaux acc-*
})
export class QueueCreateModalComponent implements OnInit, OnDestroy, OnChanges {

  ngOnInit(): void {
    document.body.classList.add('modal-open');
    this.buildForm();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('modal-open');
  }
  @Input({ required: true }) estabId!: string;
  @Input() langues: string[] = ['fr'];
  @Output() close   = new EventEmitter<void>();
  @Output() created = new EventEmitter<{ id: string; name?: string }>();

  private fb  = inject(FormBuilder);
  private api = inject(EstablishmentsService);
  private translate = inject(TranslateService);

  loading = false;
  errorMsg = '';

  form!: FormGroup;
  
  ngOnChanges(): void {
    this.buildForm();
  }
  
  private buildForm(): void {
    const controls: any = {
      name: ['', Validators.required],
      patient_message: [this.translate.instant('componentsWizard.queueCreate.patientMessageDefault')]
    };
    
    this.langues.forEach(lang => {
      if (lang !== 'fr') {
        controls[`name_${lang}`] = [''];
        controls[`patient_message_${lang}`] = [''];
      }
    });
    
    this.form = this.fb.group(controls);
  }

  submit(): void {
    if (this.loading || this.form.invalid) return;
    this.loading = true;
    this.errorMsg = '';

    const raw = this.form.getRawValue();
    
    const payload: CreateQueuePayload = {
      name: (raw.name ?? '').trim(),
      patient_message: (raw.patient_message ?? '').trim() || null
    };
    
    // Ajouter i18n seulement si plusieurs langues
    if (this.langues.length > 1) {
      const nameI18n: any = { fr: (raw.name ?? '').trim() };
      const msgI18n: any = { fr: (raw.patient_message ?? '').trim() };
      
      this.langues.forEach(lang => {
        if (lang !== 'fr') {
          nameI18n[lang] = (raw[`name_${lang}` as keyof typeof raw] as string || '').trim();
          msgI18n[lang] = (raw[`patient_message_${lang}` as keyof typeof raw] as string || '').trim();
        }
      });
      
      payload.name_i18n = JSON.stringify(nameI18n);
      payload.patient_message_i18n = JSON.stringify(msgI18n);
    }

    this.api.createQueue(this.estabId, payload).subscribe({
      next: (res) => {
        const id = (res?.id || '').toString();
        if (id) this.created.emit({ id, name: payload.name });
        this.close.emit();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.errorMsg =
          err?.error?.message ||
          err?.error?.detail ||
          (typeof err?.error === 'string' ? err.error : this.translate.instant('componentsWizard.queueCreate.errorDefault'));
      },
    });
  }

  onClose(): void {
    if (!this.loading) this.close.emit();
  }
}
